/**
 * Print Jobs API
 *
 * Submits label print jobs to the print service via the printAgent gateway prefix.
 * Each call sends one or more label items for a specific label_type.
 */
import { getApiClient } from '@/core/services/api.service';
import { config, API_ROUTES } from '@/core/config';
import { i18next, DEFAULT_NAMESPACE, DEFAULT_LANGUAGE } from '@/core/plugins/i18n';

// ── Types ──────────────────────────────────────────────────────────────────

export interface PrintJobRequest {
  label_type: string;
  device_id: string;
  data: Record<string, unknown>[];
  copies: number;
  priority: number;
  hospital_id: string;
  created_by: string;
  translations?: Record<string, string>;
}

export interface PrintJobResponse {
  success: boolean;
  total_jobs: number;
  total_items: number;
  copies_per_item: number;
  number_of_doses?: number;
  data: Array<{
    job_id: string;
    status: string;
    label_type: string;
    sec_encoded?: string | null;
    [key: string]: unknown;
  }>;
  timestamp: string;
}

/** A barcode field whose placeholder rendered empty (preview endpoint). */
export interface LabelPreviewEmptyField {
  /** field_mappings key, e.g. 'sec_encoded'. */
  key: string;
  fo: { x: number; y: number };
  /** e.g. '^BC' */
  command: string;
}

export interface LabelPreviewResponse {
  template_key: string;
  /** DPI of the template the preview used. */
  dpi: number;
  /** 'device': matches the selected printer. 'fallback': no printer given, so 203 dpi (select a printer for an exact preview). */
  dpi_source: 'device' | 'fallback';
  media_width_mm: number | null;
  media_height_mm: number | null;
  layout: Record<string, unknown>;
  /** Byte-identical to what a print job sends for the same input and printer (apart from runtime.now). */
  rendered_zpl: string;
  missing_fields: string[];
  /** Barcode fields whose data rendered empty, with their ^FO in dots (for the preview note). */
  empty_fields?: LabelPreviewEmptyField[];
  warnings: string[];
}

// Keys used as {{t.*}} variables in ZPL label templates
const PRINT_ZPL_KEYS = [
  'feedAdmin.reDoseLabel',
  'inventory.printLabel.expLabel',
  'inventory.printLabel.barcode',
  'inventory.printMilkLabel.expiryDate',
  'inventory.printMilkLabel.location',
  'common.bed',
  'patients.printLabel.dobLabel',
  'patients.printLabel.locationLabel',
  'patients.printLabel.mrnLabel',
  'patients.printLabel.nameLabel',
  'printCollection.dateField',
  'printCollection.timeField',
  'patients.printLabel.allergiesLabel',
  'printLabel.allergiesMore',
  'printLabel.allergiesNone',
  'printLabel.allergyTier.unspecified',
  'printLabel.batchLabel',
  'printLabel.bedLabel',
  'printLabel.bottleLabel',
  'printLabel.feedIdLabel',
  'printLabel.feedTypeValue',
  'printLabel.lotLabel',
  'printLabel.milkTypeLabel',
  'printLabel.pasteurizedLabel',
  'printLabel.preparedLabel',
  'printLabel.printedLabel',
  'printLabel.qtyLabel',
  'printLabel.recipeLabel',
  'printLabel.roomLabel',
  'printLabel.secLabel',
  'printLabel.skuLabel',
  'printLabel.statusLabel',
  'printLabel.typeLabel',
  'printLabel.unitLabel',
  'printLabel.volLabel',
] as const;

// Keys dropped from the locale bundle that stored ZPL templates still reference,
// mapped to the bundle key that replaced them. Templates live in tenant DBs and
// keep the old name, so unless it is supplied here the agent prints the key
// itself (e.g. "inventory.printMilkLabel.volume 12.00 mL").
const LEGACY_ZPL_KEY_ALIASES: Record<string, (typeof PRINT_ZPL_KEYS)[number]> = {
  'inventory.printMilkLabel.volume': 'printLabel.volLabel',
};

/**
 * Build the `translations` map the ZPL `{{t.*}}` variables resolve against.
 *
 * Reads the *raw* bundle rather than calling `t()`, because some of these
 * strings carry placeholders the print agent fills per item — e.g.
 * `feedAdmin.reDoseLabel` is "DOSE {{index}} of {{total}}". `t()` would
 * interpolate those to empty strings before the agent ever saw them.
 *
 * But the raw string is not shippable as-is either. `patients.printLabel.mrnLabel`
 * is "{{mrnLabel}}:", where `mrnLabel` is an i18next *defaultVariable* holding
 * the tenant's term for the medical record number (see useMrnLabel). Nothing
 * downstream knows that variable, so shipping it raw printed a literal
 * "{{mrnLabel}}:" on the label.
 *
 * So substitute exactly the defaultVariables — the app-level terms i18next
 * would always have filled in — and leave every other token for the agent.
 * Tokens carrying a format spec (`{{x, upper}}`) are left alone rather than
 * substituted without their formatter.
 */
function buildZplTranslations(): Record<string, string> | undefined {
  const rawBundle = i18next.getResourceBundle(DEFAULT_LANGUAGE, DEFAULT_NAMESPACE) as Record<
    string,
    string
  > | null;
  if (!rawBundle) return undefined;

  const defaults = (i18next.options?.interpolation?.defaultVariables ?? {}) as Record<
    string,
    unknown
  >;

  const translations: Record<string, string> = {};
  for (const key of PRINT_ZPL_KEYS) {
    const raw = rawBundle[key];
    if (typeof raw !== 'string') continue;
    translations[key] = raw.replace(/\{\{\s*([A-Za-z0-9_.]+)\s*\}\}/g, (token, name: string) =>
      Object.prototype.hasOwnProperty.call(defaults, name) ? String(defaults[name]) : token,
    );
  }

  for (const [legacyKey, key] of Object.entries(LEGACY_ZPL_KEY_ALIASES)) {
    if (key in translations) translations[legacyKey] = translations[key];
  }

  return Object.keys(translations).length > 0 ? translations : undefined;
}

/**
 * Re-dosing dose text, e.g. "DOSE 2 of 3": the same wording the print
 * controller used to generate from `number_of_doses` (the translated
 * `feedAdmin.reDoseLabel` template sent in `translations`, with {{index}} and
 * {{total}} replaced; "RE-DOSE i OF N" if the key is missing). The front end
 * now sets it per label so each selected dose prints once (SV-551-6.8).
 */
export function formatReDoseLabel(index: number, total: number): string {
  const template = buildZplTranslations()?.['feedAdmin.reDoseLabel'];
  return typeof template === 'string'
    ? template.replaceAll('{{index}}', String(index)).replaceAll('{{total}}', String(total))
    : `RE-DOSE ${index} OF ${total}`;
}

// ── API ────────────────────────────────────────────────────────────────────



/**
 * Submit a print job to the print service.
 *
 * @param params.labelType      One of the label type constants (e.g. 'FEED_LABEL')
 * @param params.data           Array of label data items matching the label type schema
 * @param params.copies         Number of copies per item (default: 1)
 * @param params.deviceId       Target printer UUID obtained from
 *                              GET {API_ROUTES.PRINT_AGENT}/devices
 *                              (i.e. usePrinterDiscovery → DiscoveredPrinter.deviceId).
 *
 * Re-dosing labels are sent one item per dose with their own dose_label; the
 * client never sends number_of_doses (which made the server expand every item
 * again, SV-551-6.8), so each item is one label.
 */
export async function submitPrintJob(params: {
  labelType: string;
  data: Record<string, unknown>[];
  copies?: number;
  deviceId: string;
}): Promise<PrintJobResponse> {
  const api = getApiClient();

  const body: PrintJobRequest = {
    label_type: params.labelType,
    device_id: params.deviceId,
    data: params.data,
    copies: params.copies ?? 1,
    priority: 5,
    hospital_id: config.hospitalId?.trim() || '',
    created_by: config.createdBy || config.staffId || 'system',
  };

  // Resolve domain-translated strings for ZPL {{t.*}} variables
  const translations = buildZplTranslations();
  if (translations) body.translations = translations;

  // The print-agent only enqueues the job so responses are fast, but large
  // payloads still need a little headroom.  Use a small per-item factor
  // (1 s) with a floor of 30 s and a hard cap of 5 minutes so a hung
  // request doesn't leave the UI overlay spinning indefinitely.
  const itemCount = params.data.length * (params.copies ?? 1); // labels × copies
  const MAX_TIMEOUT = 5 * 60_000; // 5 minutes
  const timeout = Math.min(MAX_TIMEOUT, Math.max(30_000, itemCount * 1_000));

  const resp = await api.post<PrintJobResponse>(`${API_ROUTES.PRINT_AGENT}/print-jobs`, body, { timeout });
  return resp.data;
}

/**
 * Resolve a label template preview (rendered ZPL + real base64 QR code images)
 * for the given label type and data, without creating a print job.
 *
 * @param labelType One of the label type constants (e.g. 'PATIENT_INFO_LABEL')
 * @param data      Key-value pairs matching the template's field_mappings
 * @param userName  Optional user name for `runtime.user_name` fields
 * @param deviceId  Optional printer; its DPI picks the template, as for a print job.
 *                  Without it the server uses the 203 dpi template (`dpi_source: 'fallback'`).
 * @param options   Optional `signal` to abort the request (e.g. a superseded preview batch).
 */
export async function previewLabel(
  labelType: string,
  data: Record<string, unknown>,
  userName?: string,
  deviceId?: string,
  options: { signal?: AbortSignal } = {},
): Promise<LabelPreviewResponse> {
  const api = getApiClient();
  const translations = buildZplTranslations();
  // Note: the shared axios client's response interceptor already unwraps the
  // `{ success, data }` envelope (see packages/tms-vue/src/composables/useApi.ts),
  // so `resp.data` here is already the inner LabelPreviewResponse payload.
  const url = `${API_ROUTES.PRINT_AGENT}/label-templates/${labelType}/preview`;
  const body = { data, user_name: userName, translations, device_id: deviceId };
  const resp = options.signal
    ? await api.post<LabelPreviewResponse>(url, body, { signal: options.signal })
    : await api.post<LabelPreviewResponse>(url, body);
  return resp.data;
}
